import type { ColumnTarget } from './comparison-column-slots';

export type ColumnAddRequest = {
  target: ColumnTarget;
  combinationId: number;
  cardId: string;
};

export type ColumnAddLoadingState = {
  request: ColumnAddRequest | null;
  sawPipeline: boolean;
  sawAccessoryPicker: boolean;
};

export type ColumnAddLoadingSignals = {
  pipelineActive: boolean;
  accessoryPickerOpen: boolean;
};

export const IDLE_COLUMN_ADD_LOADING: ColumnAddLoadingState = {
  request: null,
  sawPipeline: false,
  sawAccessoryPicker: false
};

export function readColumnAddCombinationId(
  combination: Record<string, unknown>
): number | null {
  const id = Number(combination.ID);
  return Number.isFinite(id) ? id : null;
}

export function beginColumnAddLoading(
  target: ColumnTarget,
  combinationId: number,
  cardId: string
): ColumnAddLoadingState {
  return {
    request: { target, combinationId, cardId },
    sawPipeline: false,
    sawAccessoryPicker: false
  };
}

export function reduceColumnAddLoading(
  state: ColumnAddLoadingState,
  signals: ColumnAddLoadingSignals
): ColumnAddLoadingState {
  if (state.request === null) return state;

  const sawPipeline = state.sawPipeline || signals.pipelineActive;
  const sawAccessoryPicker =
    state.sawAccessoryPicker || signals.accessoryPickerOpen;
  const waitingToStart = !sawPipeline && !sawAccessoryPicker;
  const waitingOnWork = signals.pipelineActive || signals.accessoryPickerOpen;

  if (waitingToStart || waitingOnWork) {
    if (
      sawPipeline === state.sawPipeline &&
      sawAccessoryPicker === state.sawAccessoryPicker
    ) {
      return state;
    }
    return { ...state, sawPipeline, sawAccessoryPicker };
  }

  return IDLE_COLUMN_ADD_LOADING;
}

export function completeColumnAddLoading(
  state: ColumnAddLoadingState,
  cardId: string
): ColumnAddLoadingState {
  if (state.request?.cardId !== cardId) return state;
  return IDLE_COLUMN_ADD_LOADING;
}

function columnAddRequestMatchesTarget(
  request: ColumnAddRequest | null,
  target: ColumnTarget
): boolean {
  if (request === null || request.target.kind !== target.kind) return false;
  if (request.target.kind === 'add' || target.kind === 'add') return true;
  return request.target.replaceSystemId === target.replaceSystemId;
}

export function clearColumnAddLoadingTarget(
  state: ColumnAddLoadingState,
  target: ColumnTarget
): ColumnAddLoadingState {
  if (!columnAddRequestMatchesTarget(state.request, target)) return state;
  return IDLE_COLUMN_ADD_LOADING;
}

export function columnAddLoadingForSystem(
  state: ColumnAddLoadingState,
  systemId: string
): boolean {
  return (
    state.request?.target.kind === 'swap' &&
    state.request.target.replaceSystemId === systemId
  );
}

export function columnAddLoadingForAddSlot(
  state: ColumnAddLoadingState,
  slotIndex: number,
  firstEmptySlotIndex: number
): boolean {
  return (
    state.request?.target.kind === 'add' && slotIndex === firstEmptySlotIndex
  );
}
